/**
 * @file ContractDataPage.jsx
 * @description Contract data — for SEEING and CORRECTING contract records.
 *              Complete (every stored field, full machine-contract history,
 *              customer record) but presented as a clean UI: labelled tiles,
 *              formatted dates/amounts, shift + status chips — not a raw DB
 *              dump. No map, no telemetry. Edit opens the contract edit dialog.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box, Card, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, IconButton, Button, Skeleton, Stack, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Snackbar,
    Breadcrumbs, Link as MuiLink, Collapse, Autocomplete, Divider,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Description as DescriptionIcon,
    PrecisionManufacturing as EquipmentIcon,
    Apartment as ApartmentIcon,
    Refresh as RefreshIcon,
    ChevronRight as ChevronRightIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import mewpService from '../../services/mewpService';
import rentalService from '../../services/rentalService';
import client from '../../api/client';

// ── formatting ────────────────────────────────────────────────────────────────
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (v) => {
    if (!v) return null;
    const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
    if (!m) return String(v);
    const d = `${parseInt(m[3], 10)} ${MONTHS[parseInt(m[2], 10) - 1]} ${m[1]}`;
    return (m[4] && !(m[4] === '00' && m[5] === '00')) ? `${d} · ${m[4]}:${m[5]}` : d;
};
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

// Friendly labels for known columns; anything else gets Title Case.
const LABELS = {
    id: 'Record ID', rental_contract_id: 'Contract ID', sub_contract_id: 'Machine Contract ID',
    customer_id: 'Customer ID', equipment_id: 'Machine No.', operator_id: 'Operator',
    contract_signed_date: 'Signed Date', contract_start: 'Start Date', contract_end: 'End Date',
    rental_type: 'Rental Type', hourly_rate: 'Hourly Rate', monthly_rate: 'Monthly Rate',
    advance_payment: 'Advance', contract_status: 'Status', site_location: 'Site Location',
    geofence_polygon: 'Geofence', geofence_enabled: 'Geofence Alarms',
    shift_start_time: 'Shift Start', shift_end_time: 'Shift End', shifts: 'Shifts',
    effective_from_date: 'From', effective_to_date: 'To', status: 'Status',
    created_at: 'Created', updated_at: 'Updated', company_name: 'Company',
    contact_person: 'Contact Person', billing_address: 'Billing Address', gst_number: 'GST Number',
};
const labelOf = (k) => LABELS[k] || String(k).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const STATUS_COLORS = {
    ACTIVE: '#16a34a', AMENDED: '#d97706', CANCELLED: '#dc2626',
    COMPLETED: '#2563eb', INACTIVE: '#6b7280', DRAFT: '#6b7280',
};
const StatusChip = ({ value }) => {
    const c = STATUS_COLORS[String(value).toUpperCase()] || '#6b7280';
    return <Chip size="small" label={String(value)} sx={{ height: 22, fontWeight: 800, fontSize: 11, bgcolor: `${c}15`, color: c }} />;
};

const shiftChips = (row) => {
    let list = [];
    try {
        const arr = typeof row.shifts === 'string' ? JSON.parse(row.shifts) : row.shifts;
        if (Array.isArray(arr) && arr.length) list = arr.map(s => `${String(s.start_time).slice(0, 5)}–${String(s.end_time).slice(0, 5)}`);
    } catch (e) { /* fall back below */ }
    if (!list.length && (row.shift_start_time || row.shift_end_time)) {
        list = [`${String(row.shift_start_time || '').slice(0, 5)}–${String(row.shift_end_time || '').slice(0, 5)}`];
    }
    return list;
};

// One value, rendered nicely by what the column means.
const niceValue = (k, v) => {
    if (v === null || v === undefined || v === '') return <Typography component="span" color="text.disabled">—</Typography>;
    if (/status/i.test(k)) return <StatusChip value={v} />;
    if (k === 'geofence_polygon') {
        try { const pts = typeof v === 'string' ? JSON.parse(v) : v; return <Chip size="small" label={`Polygon · ${Array.isArray(pts) ? pts.length : 0} points`} sx={{ height: 22, fontWeight: 700 }} />; }
        catch (e) { return <Chip size="small" label="Polygon" sx={{ height: 22 }} />; }
    }
    if (k === 'geofence_enabled') return <Chip size="small" label={Number(v) ? 'Enabled' : 'Disabled'} sx={{ height: 22, fontWeight: 700, bgcolor: Number(v) ? '#16a34a15' : '#6b728015', color: Number(v) ? '#16a34a' : '#6b7280' }} />;
    if (/(rate|advance|amount|payment)/i.test(k) && !isNaN(parseFloat(v))) return <>{inr(v)}</>;
    if (/(date|_at$|_start$|_end$)/i.test(k)) { const d = fmtDate(v); if (d) return <>{d}</>; }
    if (typeof v === 'object') return <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{JSON.stringify(v)}</Typography>;
    return <>{String(v)}</>;
};

const SectionCard = ({ icon, title, count, children }) => (
    <Card sx={{ borderRadius: '16px', mb: 2.5, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.75, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
            <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700 }}>{title}</Typography>
            {count != null && (
                <Box component="span" sx={{ fontWeight: 700, fontSize: 11.5, color: 'primary.main', bgcolor: 'rgba(37,99,235,0.08)', px: 1.125, py: 0.25, borderRadius: '7px' }}>
                    {count}
                </Box>
            )}
        </Box>
        {children}
    </Card>
);

// Labelled tile grid for a single record (main contract / customer).
const TileGrid = ({ row, omit = [] }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: '18px 30px', px: 2.75, py: 2.5 }}>
        {Object.entries(row).filter(([k]) => !omit.includes(k)).map(([k, v]) => (
            <Box key={k} sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 10.5, letterSpacing: '.07em', textTransform: 'uppercase', color: 'text.disabled', mb: 0.375 }}>
                    {labelOf(k)}
                </Typography>
                <Typography component="div" sx={{ fontWeight: 600, fontSize: 14, wordBreak: 'break-word' }}>
                    {niceValue(k, v)}
                </Typography>
            </Box>
        ))}
    </Box>
);

export default function ContractDataPage() {
    const { contractId } = useParams();
    const navigate = useNavigate();
    // ?machine=A01529 → machine-contract mode: show ONLY that machine's
    // contract rows (its full history on this contract), no main-contract block.
    const [searchParams] = useSearchParams();
    const machineFilter = (searchParams.get('machine') || '').trim();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Machine-contract row editor: edits ONE ced row in place.
    const [editRow, setEditRow] = useState(null);       // the row being edited
    const [editForm, setEditForm] = useState(null);     // { from, to, status, shifts:[{start,end,from_date,to_date}] }
    const [saving, setSaving] = useState(false);
    // Operators on the machine being edited (machine_operators assignments) —
    // operators can change mid-contract, so each has its own from/to dates.
    const [editOps, setEditOps] = useState([]);
    const [opsRoster, setOpsRoster] = useState([]);     // operators master (emp roster) for the picker
    const [newOp, setNewOp] = useState(null);           // roster pick pending Add
    const [newOpFrom, setNewOpFrom] = useState('');     // its from date
    const [snack, setSnack] = useState('');
    const [expanded, setExpanded] = useState({});       // equipment_id -> history open?
    const [delRow, setDelRow] = useState(null);         // row pending deletion

    const confirmDelete = async () => {
        if (!delRow) return;
        setSaving(true);
        try {
            await mewpService.deleteMachineContractRow(delRow.id);
            setSnack(`Deleted row #${delRow.id}.`);
            setDelRow(null);
            load();
        } catch (e) {
            setSnack(e.message || 'Delete failed');
        } finally {
            setSaving(false);
        }
    };

    const toYmd = (v) => (v ? String(v).slice(0, 10) : '');
    // Shifts from the RAW stored JSON so the per-shift effective dates survive
    // (shiftChips flattens them to "HH:MM–HH:MM" labels only).
    const rawShifts = (r) => {
        try {
            const arr = typeof r.shifts === 'string' ? JSON.parse(r.shifts) : r.shifts;
            if (Array.isArray(arr) && arr.length) {
                return arr.map(s => ({
                    start_time: String(s.start_time || '08:00').slice(0, 5),
                    end_time: String(s.end_time || '18:00').slice(0, 5),
                    from_date: toYmd(s.from_date),
                    to_date: toYmd(s.to_date),
                }));
            }
        } catch (e) { /* fall through */ }
        return [{
            start_time: String(r.shift_start_time || '08:00').slice(0, 5),
            end_time: String(r.shift_end_time || '18:00').slice(0, 5),
            from_date: '', to_date: '',
        }];
    };

    const refreshEditOps = async (equipmentId) => {
        try {
            const res = await rentalService.getOperators(equipmentId);
            setEditOps(res.operators || []);
        } catch (e) { setEditOps([]); }
    };

    const openRowEditor = (r) => {
        setEditRow(r);
        setEditForm({
            from: toYmd(r.effective_from_date),
            to: toYmd(r.effective_to_date),
            status: String(r.status || 'ACTIVE').toUpperCase(),
            shifts: rawShifts(r),
        });
        // operators section — current assignments + roster for the picker
        setNewOp(null);
        setNewOpFrom(new Date().toISOString().slice(0, 10));
        refreshEditOps(r.equipment_id);
        if (!opsRoster.length) {
            client.get('/users/operators')
                .then(res => setOpsRoster(res.data || []))
                .catch(() => setOpsRoster([]));
        }
    };

    // ---- operator assignment actions (apply immediately, list refreshes) ----
    const addOperator = async () => {
        if (!editRow || !newOp) return;
        try {
            await rentalService.addOperator(editRow.equipment_id, {
                operator_name: `${newOp.name} (${newOp.user_id_code})`,
                from_date: newOpFrom || null,
            });
            setNewOp(null);
            refreshEditOps(editRow.equipment_id);
        } catch (e) { setSnack(e.message || 'Failed to add operator'); }
    };
    const setOperatorDate = async (op, field, value) => {
        try {
            await rentalService.updateOperator(op.id, { [field]: value || null });
            refreshEditOps(editRow.equipment_id);
        } catch (e) { setSnack(e.message || 'Failed to update operator'); }
    };
    const removeOperator = async (op) => {
        try {
            await rentalService.deleteOperator(op.id);
            refreshEditOps(editRow.equipment_id);
        } catch (e) { setSnack(e.message || 'Failed to remove operator'); }
    };

    const saveRowEditor = async () => {
        if (!editRow || !editForm) return;
        setSaving(true);
        try {
            await mewpService.updateMachineContractRow(editRow.id, {
                effective_from_date: editForm.from || null,
                effective_to_date: editForm.to || null,
                status: editForm.status,
                shifts: editForm.shifts.map(s => ({
                    start_time: s.start_time, end_time: s.end_time,
                    ...(s.from_date ? { from_date: s.from_date } : {}),
                    ...(s.to_date ? { to_date: s.to_date } : {}),
                })),
            });
            setSnack('Machine contract updated.');
            setEditRow(null); setEditForm(null);
            load();
        } catch (e) {
            setSnack(e.message || 'Update failed');
        } finally {
            setSaving(false);
        }
    };

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await mewpService.getContractRaw(contractId);
            setData(res);
        } catch (e) {
            setError(e.message || 'Failed to load contract data');
        } finally {
            setLoading(false);
        }
    }, [contractId]);

    useEffect(() => { load(); }, [load]);

    const allEqRows = data?.equipment_details || [];
    const eqRows = machineFilter
        ? allEqRows.filter(r => String(r.equipment_id || '').trim() === machineFilter)
        : allEqRows;

    // Group the rows: ONE line per machine; expanding shows its version history
    // (replaced / changed machine-contract rows) with per-row edit + delete.
    const groups = [];
    {
        const map = new Map();
        for (const r of eqRows) {
            const k = String(r.equipment_id || '—').trim();
            if (!map.has(k)) { const g = { equipment_id: k, serial_number: r.serial_number, rows: [] }; map.set(k, g); groups.push(g); }
            map.get(k).rows.push(r);
        }
        for (const g of groups) {
            g.current = [...g.rows].reverse().find(r => String(r.status).toUpperCase() === 'ACTIVE') || g.rows[g.rows.length - 1];
            g.from = g.rows.reduce((m, r) => (!m || (r.effective_from_date && String(r.effective_from_date) < String(m))) ? r.effective_from_date : m, null);
        }
    }

    const displayMachine = (groups[0]?.serial_number) || machineFilter;
    // Breadcrumb id: the machine's contract line id (RC-2026-010-03) in machine
    // mode, else the main contract id.
    const crumbId = machineFilter
        ? ((eqRows[0] && eqRows[0].sub_contract_id) || machineFilter)
        : contractId;

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }} className="animate-fade-in">
            {/* Breadcrumbs: Rentals / Machine Contracts / RC-…-03 (machine mode)
                or Rentals / Contract Details / RC-… (full mode) */}
            <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />} sx={{ mb: 1.75 }}>
                <MuiLink component="button" onClick={() => navigate('/rentals')} underline="hover" color="primary" sx={{ fontWeight: 600 }}>
                    Rentals
                </MuiLink>
                <MuiLink component="button" underline="hover" color="primary" sx={{ fontWeight: 600 }}
                    onClick={() => navigate(machineFilter ? '/rentals?view=machineContracts' : '/rentals?view=contractData')}>
                    {machineFilter ? 'Machine Contracts' : 'Contract Details'}
                </MuiLink>
                <Typography color="text.secondary" sx={{ fontWeight: 600 }}>{crumbId}</Typography>
            </Breadcrumbs>

            {/* Header */}
            <Stack direction="row" alignItems="center" gap={2} sx={{ mb: 2.5 }} flexWrap="wrap">
                <IconButton onClick={() => navigate(-1)} aria-label="Back"
                    sx={{ width: 44, height: 44, borderRadius: '13px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                    <Typography variant="h5" fontWeight={800}>
                        {machineFilter ? `${displayMachine} · ${contractId}` : contractId}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {machineFilter
                            ? 'Machine contract record — check the data and use Edit to correct it.'
                            : 'Full contract record — check the data and use Edit to correct it.'}
                    </Typography>
                </Box>
                {machineFilter && (
                    <Button variant="outlined" sx={{ fontWeight: 700 }}
                        onClick={() => navigate(`/contract-data/${contractId}`)}>
                        View full contract
                    </Button>
                )}
                <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
                {machineFilter ? (
                    // Machine mode edits ONLY this machine's row(s) — the main
                    // contract is not editable from here.
                    <Button variant="contained" startIcon={<EditIcon />} sx={{ fontWeight: 700 }}
                        disabled={loading || !groups.length}
                        onClick={() => groups.length && openRowEditor(groups[0].current)}>
                        Edit Machine Contract
                    </Button>
                ) : (
                    <Button variant="contained" startIcon={<EditIcon />} sx={{ fontWeight: 700 }}
                        onClick={() => navigate(`/contract/${contractId}`)}>
                        Edit Contract
                    </Button>
                )}
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <>
                    <Skeleton variant="rounded" height={240} sx={{ mb: 2.5, borderRadius: '16px' }} />
                    <Skeleton variant="rounded" height={320} sx={{ borderRadius: '16px' }} />
                </>
            ) : data && (
                <>
                    {/* Main contract — hidden in machine-contract mode */}
                    {!machineFilter && (
                        <SectionCard icon={<DescriptionIcon sx={{ fontSize: 20 }} />} title="Main Contract">
                            <TileGrid row={data.contract || {}} />
                        </SectionCard>
                    )}

                    {/* Machine contracts — ONE line per machine; expand for the
                        version history (replaced/changed rows) with edit + delete */}
                    <SectionCard icon={<EquipmentIcon sx={{ fontSize: 20 }} />}
                        title={machineFilter ? `Machine Contract — ${displayMachine}` : 'Machine Contracts'}
                        count={`${groups.length} machine${groups.length === 1 ? '' : 's'}`}>
                        {groups.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">No machine contract rows.</Typography>
                            </Box>
                        ) : groups.map((g) => {
                            const cur = g.current || {};
                            const curSt = String(cur.status || '').toUpperCase();
                            const chips = shiftChips(cur);
                            const open = expanded[g.equipment_id] ?? !!machineFilter;
                            const history = g.rows.filter(r => r.id !== cur.id);
                            return (
                                <Box key={g.equipment_id} sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } }}>
                                    {/* Machine summary line (current terms) */}
                                    <Box onClick={() => setExpanded(e => ({ ...e, [g.equipment_id]: !open }))}
                                        sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.75, py: 1.75, cursor: 'pointer', flexWrap: 'wrap', '&:hover': { bgcolor: 'rgba(37,99,235,0.03)' } }}>
                                        <Box sx={{ minWidth: 170 }}>
                                            <Typography variant="body2" fontWeight={800}>{g.serial_number || g.equipment_id}</Typography>
                                            {g.serial_number && <Typography variant="caption" color="text.disabled">ID: {g.equipment_id}</Typography>}
                                        </Box>
                                        <Box sx={{ minWidth: 150 }}>
                                            <Typography variant="caption" color="text.disabled" display="block">Machine Contract</Typography>
                                            <Typography variant="body2" fontWeight={700} sx={{ color: 'primary.main' }}>{cur.sub_contract_id || '—'}</Typography>
                                        </Box>
                                        <Box sx={{ minWidth: 190 }}>
                                            <Typography variant="caption" color="text.disabled" display="block">Period</Typography>
                                            <Typography variant="body2" fontWeight={600}>
                                                {fmtDate(g.from) || '—'} → {cur.effective_to_date && curSt !== 'ACTIVE' ? fmtDate(cur.effective_to_date) : (cur.effective_to_date ? fmtDate(cur.effective_to_date) : 'Ongoing')}
                                            </Typography>
                                        </Box>
                                        <Stack spacing={0.5} sx={{ minWidth: 140 }} alignItems="flex-start">
                                            {chips.length ? chips.map((s, j) => (
                                                <Box key={j} component="span" sx={{ px: 1, py: 0.25, borderRadius: '7px', bgcolor: '#f1f5f9', fontWeight: 600, fontSize: 11.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                                    S{j + 1} {s}
                                                </Box>
                                            )) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                        </Stack>
                                        <StatusChip value={curSt || '—'} />
                                        <Box sx={{ flexGrow: 1 }} />
                                        {history.length > 0 && (
                                            <Chip size="small" label={`${history.length} change${history.length === 1 ? '' : 's'}`}
                                                sx={{ height: 20, fontWeight: 700, bgcolor: '#f1f5f9', color: 'text.secondary' }} />
                                        )}
                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); openRowEditor(cur); }} aria-label="Edit current">
                                            <EditIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                        {open ? <ExpandLessIcon sx={{ color: 'text.disabled' }} /> : <ExpandMoreIcon sx={{ color: 'text.disabled' }} />}
                                    </Box>

                                    {/* Version history: replaced / changed rows */}
                                    <Collapse in={open} timeout="auto" unmountOnExit>
                                        <Box sx={{ px: 2.75, pb: 2, bgcolor: 'rgba(148,163,184,0.04)' }}>
                                            <TableContainer sx={{ overflowX: 'auto' }}>
                                                <Table size="small" sx={{ minWidth: 760 }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Row</TableCell>
                                                            <TableCell>From</TableCell>
                                                            <TableCell>To</TableCell>
                                                            <TableCell>Shift</TableCell>
                                                            <TableCell>Status</TableCell>
                                                            <TableCell>Updated</TableCell>
                                                            <TableCell align="right">Actions</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {g.rows.map((r) => {
                                                            const st = String(r.status || '').toUpperCase();
                                                            const rchips = shiftChips(r);
                                                            const isCur = r.id === cur.id;
                                                            return (
                                                                <TableRow key={r.id} hover sx={isCur ? { bgcolor: 'rgba(22,163,74,0.05)' } : undefined}>
                                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                                        <Typography variant="body2" fontWeight={700}>#{r.id}</Typography>
                                                                        {isCur && <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 700 }}>current</Typography>}
                                                                    </TableCell>
                                                                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{fmtDate(r.effective_from_date) || '—'}</TableCell>
                                                                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{r.effective_to_date ? fmtDate(r.effective_to_date) : 'Ongoing'}</TableCell>
                                                                    <TableCell>
                                                                        <Stack spacing={0.5} alignItems="flex-start">
                                                                            {rchips.length ? rchips.map((s, j) => (
                                                                                <Box key={j} component="span" sx={{ px: 1, py: 0.25, borderRadius: '7px', bgcolor: '#f1f5f9', fontWeight: 600, fontSize: 11.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                                                                    S{j + 1} {s}
                                                                                </Box>
                                                                            )) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                                                        </Stack>
                                                                    </TableCell>
                                                                    <TableCell><StatusChip value={st || '—'} /></TableCell>
                                                                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{fmtDate(r.updated_at) || '—'}</TableCell>
                                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                                        <IconButton size="small" onClick={() => openRowEditor(r)} aria-label="Edit row">
                                                                            <EditIcon sx={{ fontSize: 17 }} />
                                                                        </IconButton>
                                                                        <IconButton size="small" color="error" onClick={() => setDelRow(r)} aria-label="Delete row">
                                                                            <DeleteIcon sx={{ fontSize: 17 }} />
                                                                        </IconButton>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </SectionCard>

                    {/* Customer record — hidden in machine-contract mode */}
                    {!machineFilter && data.customer && (
                        <SectionCard icon={<ApartmentIcon sx={{ fontSize: 20 }} />} title="Customer">
                            <TileGrid row={data.customer} />
                        </SectionCard>
                    )}
                </>
            )}

            {/* Machine-contract row editor — corrects ONE row, main contract untouched */}
            <Dialog open={!!editRow} onClose={() => !saving && setEditRow(null)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 800 }}>
                    Edit Machine Contract
                    <Typography variant="body2" color="text.secondary">
                        {editRow?.sub_contract_id} · {editRow?.serial_number || editRow?.equipment_id} (row #{editRow?.id})
                    </Typography>
                </DialogTitle>
                {editForm && (
                    <DialogContent sx={{ pt: 1 }}>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Stack direction="row" spacing={2}>
                                <TextField fullWidth type="date" label="From" value={editForm.from}
                                    onChange={(e) => setEditForm(f => ({ ...f, from: e.target.value }))}
                                    InputLabelProps={{ shrink: true }} />
                                <TextField fullWidth type="date" label="To (empty = ongoing)" value={editForm.to}
                                    onChange={(e) => setEditForm(f => ({ ...f, to: e.target.value }))}
                                    InputLabelProps={{ shrink: true }} />
                            </Stack>
                            <TextField select fullWidth label="Status" value={editForm.status}
                                onChange={(e) => setEditForm(f => ({ ...f, status: e.target.value }))}>
                                {['ACTIVE', 'AMENDED', 'CANCELLED', 'COMPLETED', 'INACTIVE'].map(s => (
                                    <MenuItem key={s} value={s}>{s}</MenuItem>
                                ))}
                            </TextField>
                            <Box>
                                <Typography sx={{ fontWeight: 700, fontSize: 12, color: 'text.secondary', mb: 1 }}>
                                    Shifts
                                    <Typography component="span" sx={{ fontWeight: 500, fontSize: 11, color: 'text.disabled', ml: 0.75 }}>
                                        — dates optional; a dated shift applies only in that period
                                    </Typography>
                                </Typography>
                                <Stack spacing={1.25}>
                                    {editForm.shifts.map((s, i) => (
                                        <Box key={i} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', p: 1.25 }}>
                                            <Stack direction="row" spacing={1.25} alignItems="center">
                                                <TextField size="small" type="time" label={`S${i + 1} start`} value={s.start_time}
                                                    onChange={(e) => setEditForm(f => { const shifts = [...f.shifts]; shifts[i] = { ...shifts[i], start_time: e.target.value }; return { ...f, shifts }; })}
                                                    InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                                                <TextField size="small" type="time" label={`S${i + 1} end`} value={s.end_time}
                                                    onChange={(e) => setEditForm(f => { const shifts = [...f.shifts]; shifts[i] = { ...shifts[i], end_time: e.target.value }; return { ...f, shifts }; })}
                                                    InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                                                <Button size="small" color="error" disabled={editForm.shifts.length <= 1}
                                                    onClick={() => setEditForm(f => ({ ...f, shifts: f.shifts.filter((_, j) => j !== i) }))}>
                                                    Remove
                                                </Button>
                                            </Stack>
                                            <Stack direction="row" spacing={1.25} sx={{ mt: 1.25 }}>
                                                <TextField size="small" type="date" label="Effective from" value={s.from_date || ''}
                                                    onChange={(e) => setEditForm(f => { const shifts = [...f.shifts]; shifts[i] = { ...shifts[i], from_date: e.target.value }; return { ...f, shifts }; })}
                                                    InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                                                <TextField size="small" type="date" label="Effective to" value={s.to_date || ''}
                                                    onChange={(e) => setEditForm(f => { const shifts = [...f.shifts]; shifts[i] = { ...shifts[i], to_date: e.target.value }; return { ...f, shifts }; })}
                                                    InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                                            </Stack>
                                        </Box>
                                    ))}
                                    <Button size="small" disabled={editForm.shifts.length >= 3}
                                        onClick={() => setEditForm(f => ({ ...f, shifts: [...f.shifts, { start_time: '20:00', end_time: '06:00', from_date: '', to_date: '' }] }))}
                                        sx={{ alignSelf: 'flex-start', fontWeight: 700 }}>
                                        + Add shift
                                    </Button>
                                </Stack>
                            </Box>

                            <Divider />

                            {/* Operators — assignments change instantly (not on Save) */}
                            <Box>
                                <Typography sx={{ fontWeight: 700, fontSize: 12, color: 'text.secondary', mb: 1 }}>
                                    Operators
                                    <Typography component="span" sx={{ fontWeight: 500, fontSize: 11, color: 'text.disabled', ml: 0.75 }}>
                                        — changes apply immediately
                                    </Typography>
                                </Typography>
                                <Stack spacing={1.25}>
                                    {editOps.length === 0 && (
                                        <Typography variant="caption" color="text.disabled">No operators assigned yet.</Typography>
                                    )}
                                    {editOps.map((op) => (
                                        <Box key={op.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', p: 1.25,
                                            opacity: op.active ? 1 : 0.65 }}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap title={op.operator_name}>
                                                    {op.operator_name}
                                                </Typography>
                                                {!op.active && <Chip size="small" label="ENDED" sx={{ height: 18, fontSize: 10, fontWeight: 800 }} />}
                                                <Button size="small" color="error" onClick={() => removeOperator(op)}>Remove</Button>
                                            </Stack>
                                            <Stack direction="row" spacing={1.25} sx={{ mt: 1 }}>
                                                <TextField size="small" type="date" label="From" value={op.from_date || ''}
                                                    onChange={(e) => setOperatorDate(op, 'from_date', e.target.value)}
                                                    InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                                                <TextField size="small" type="date" label="To (empty = active)" value={op.to_date || ''}
                                                    onChange={(e) => setOperatorDate(op, 'to_date', e.target.value)}
                                                    InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                                            </Stack>
                                        </Box>
                                    ))}
                                    <Stack direction="row" spacing={1.25} alignItems="center">
                                        <Autocomplete
                                            size="small" sx={{ flex: 1.4 }}
                                            options={opsRoster}
                                            value={newOp}
                                            onChange={(e, v) => setNewOp(v)}
                                            getOptionLabel={(o) => o ? `${o.name} (${o.user_id_code})` : ''}
                                            isOptionEqualToValue={(o, v) => o.id === v.id}
                                            renderInput={(params) => <TextField {...params} label="Add operator" placeholder="Name / Emp ID" />}
                                        />
                                        <TextField size="small" type="date" label="From" value={newOpFrom}
                                            onChange={(e) => setNewOpFrom(e.target.value)}
                                            InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                                        <Button size="small" variant="contained" disabled={!newOp} onClick={addOperator} sx={{ fontWeight: 700 }}>
                                            Add
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Box>
                        </Stack>
                    </DialogContent>
                )}
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEditRow(null)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={saveRowEditor} disabled={saving} sx={{ fontWeight: 700 }}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirmation — permanent removal of one history row */}
            <Dialog open={!!delRow} onClose={() => !saving && setDelRow(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Delete machine contract row?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Row <b>#{delRow?.id}</b> — {delRow?.sub_contract_id} · {delRow?.serial_number || delRow?.equipment_id}
                        {' '}({String(delRow?.status || '').toUpperCase()}, {fmtDate(delRow?.effective_from_date) || '—'} → {delRow?.effective_to_date ? fmtDate(delRow?.effective_to_date) : 'Ongoing'})
                    </Typography>
                    <Alert severity={String(delRow?.status || '').toUpperCase() === 'ACTIVE' ? 'error' : 'warning'}>
                        {String(delRow?.status || '').toUpperCase() === 'ACTIVE'
                            ? 'This is the machine’s CURRENT line — deleting it removes the machine from the contract entirely.'
                            : 'This permanently removes this history line. Reports will no longer see this period’s terms.'}
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDelRow(null)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={confirmDelete} disabled={saving} sx={{ fontWeight: 700 }}>
                        {saving ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack('')}
                message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
        </Box>
    );
}
